import type { CatalogSortColumn } from './catalogSortColumns';
import type { ColumnKey } from '../../../components/TrackClipTable';

/** The server sort state driven by the combined Title/Artist column header. */
export interface TitleArtistSort {
  sortBy: CatalogSortColumn;
  sortOrder: 'asc' | 'desc';
}

/**
 * Click cycle for the Title/Artist column header. Each click advances one step:
 * Title ascending → Title descending → Artist ascending → Artist descending →
 * cleared, then wraps back to the start.
 */
export const TITLE_ARTIST_SORT_CYCLE: TitleArtistSort[] = [
  { sortBy: 'title', sortOrder: 'asc' },
  { sortBy: 'title', sortOrder: 'desc' },
  { sortBy: 'artist', sortOrder: 'asc' },
  { sortBy: 'artist', sortOrder: 'desc' },
  { sortBy: null, sortOrder: 'asc' }, // cleared
];

/**
 * The next sort state when the Title/Artist header is clicked. Any state outside
 * the cycle — a cleared sort, or one set by another control — starts the cycle
 * over at Title ascending.
 */
export const nextTitleArtistSort = (current: TitleArtistSort): TitleArtistSort => {
  const index = TITLE_ARTIST_SORT_CYCLE.findIndex(
    (state) =>
      state.sortBy === current.sortBy &&
      (state.sortBy === null || state.sortOrder === current.sortOrder),
  );
  return TITLE_ARTIST_SORT_CYCLE[(index + 1) % TITLE_ARTIST_SORT_CYCLE.length];
};

/**
 * Standard asc/desc-sortable table columns mapped to their server sort key.
 * These use the conventional column-header toggle (ascending → descending →
 * cleared), unlike the combined Title/Artist header above.
 */
export const SORTABLE_COLUMN_FIELDS = {
  album: 'release',
  explicitness: 'explicitness',
  dateAdded: 'create_date',
} satisfies Partial<Record<ColumnKey, CatalogSortColumn>>;

export type SortableColumnKey = keyof typeof SORTABLE_COLUMN_FIELDS;

/** The column keys that render a standard sort toggle. */
export const SORTABLE_COLUMNS = Object.keys(
  SORTABLE_COLUMN_FIELDS,
) as SortableColumnKey[];

/** Reverse-map the active server sort key back to its table column, if standard. */
export const columnForSort = (
  sortBy: CatalogSortColumn,
): SortableColumnKey | null =>
  SORTABLE_COLUMNS.find((column) => SORTABLE_COLUMN_FIELDS[column] === sortBy) ??
  null;
